import { useEffect, useRef, useState } from 'react';

import {
    Box,
    Button,
    Checkbox,
    // Typography,
    Link,
    Typography,
} from '@mui/material';
import { Redirect, Link as RouterLink } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import LoginField from 'src/components/LoginField';
import PasswordField from 'src/components/PasswordField';
import WidgetWrapper from 'src/components/FormWrapper';
// import TelegramButton from 'src/widgets/TelegramButton'
// import ButtonText from 'src/components/ButtonText'
import useGoTo from 'src/hooks/useGoTo';
import ROUTES from 'src/router/routes';

import scheme, { type TypeRegisterScheme } from 'src/validations/register';
import useSessionStore from 'src/stores/session';
import CustomAccordion from 'src/components/CustomAccordion';

import { ROUTES_URL } from 'src/constants/routes';

import { useMutation } from 'react-query';
import SessionService from 'src/services/SessionService';
import {
    ANALYTICS_CONSENT_CHANGE_EVENT,
    analyticsConsentChoice,
    recordRegistrationStartedObservation,
    recordRegistrationSuccessGoal,
    setAnalyticsConsentChoice,
} from 'src/services/AcquisitionBrowserBridge';

import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import TurnstileWidget from 'src/components/TurnstileWidget';
import { captureAttributionId, clearAttributionId } from 'src/utils/attribution';
import { capturePublicLaunchIntent } from 'src/utils/publicLaunchIntent';
import { readIntentHandoff } from 'src/utils/intentHandoff';
import { useSnackbar } from 'notistack';

import type { SubmitHandler } from 'react-hook-form';

export default function RegisterForm() {
    const goTo = useGoTo();
    const { enqueueSnackbar } = useSnackbar();
    const [attributionId] = useState(() => captureAttributionId());
    const [publicLaunchIntent] = useState(() => capturePublicLaunchIntent());
    const turnstileSiteKey = (process.env.REACT_APP_TURNSTILE_SITE_KEY || '').trim();
    const [captchaToken, setCaptchaToken] = useState('');
    const [turnstileResetKey, setTurnstileResetKey] = useState(0);
    const [marketingConsent, setMarketingConsent] = useState(false);
    const [analyticsConsent, setAnalyticsConsent] = useState(() => analyticsConsentChoice() === 'accepted');
    const registrationStarted = useRef(false);

    const recordFirstInteraction = () => {
        if (registrationStarted.current) return;
        registrationStarted.current = recordRegistrationStartedObservation();
    };

    useEffect(() => {
        const synchronizeAnalyticsConsent = () => setAnalyticsConsent(analyticsConsentChoice() === 'accepted');
        window.addEventListener(ANALYTICS_CONSENT_CHANGE_EVENT, synchronizeAnalyticsConsent);
        window.addEventListener('focus', synchronizeAnalyticsConsent);
        return () => {
            window.removeEventListener(ANALYTICS_CONSENT_CHANGE_EVENT, synchronizeAnalyticsConsent);
            window.removeEventListener('focus', synchronizeAnalyticsConsent);
        };
    }, []);

    const changeAnalyticsConsent = (checked: boolean) => {
        setAnalyticsConsentChoice(checked ? 'accepted' : 'rejected');
        setAnalyticsConsent(checked);
        if (checked) recordFirstInteraction();
    };

    const session = useSessionStore(({ isAuthorized }) => ({
        isAuthorized,
    }));

    const {
        control,
        handleSubmit,
        setError,
        formState: { errors, isValid },
    } = useForm<TypeRegisterScheme>({
        resolver: zodResolver(scheme),
        resetOptions: {
            keepIsSubmitted: true,
        },
    });

    const { mutateAsync: login, isLoading: isLoginLoading } = useMutation(
        SessionService.queryKey,
        useSessionStore.getState().login,
        {
            onError: () => {
                goTo(ROUTES_URL.LOGIN);
            },
            onSuccess: () => {
                goTo(ROUTES_URL.WELCOME_SCREEN);
            },
        }
    );

    const { mutateAsync: register, isLoading: isRegisterLoading } = useMutation(
        SessionService.queryKey,
        useSessionStore.getState().register,
        {
            onError: (error: Obj) => {
                if (turnstileSiteKey) {
                    setCaptchaToken('');
                    setTurnstileResetKey((key) => key + 1);
                }
                Object.keys(error).forEach((key) => {
                    const value = error[key];
                    const message = Array.isArray(value) ? value[0] : value;
                    if (key === 'captcha_token' || key === 'detail') {
                        enqueueSnackbar(String(message), { variant: 'error' });
                    } else {
                        setError((key === 'username' ? 'email' : key) as any, { message: String(message) });
                    }
                });
            },
        }
    );

    const onSubmit: SubmitHandler<TypeRegisterScheme> = async (data) => {
        try {
            await register({
                ...data,
                username: data.email,
                lm_attribution_id: attributionId || undefined,
                captcha_token: captchaToken || undefined,
                marketing_consent: marketingConsent,
            });
            recordRegistrationSuccessGoal();
            clearAttributionId();
            await login({ email: data.email, password: data.password });
        } catch {}
    };

    const isLoading = isRegisterLoading || isLoginLoading;

    if (session.isAuthorized) return <Redirect to={'/'} />;

    return (
        <LoadingWrapper isLoading={isLoading}>
            <WidgetWrapper
                name="Регистрация"
                topGutter
            >
                <form
                    aria-label="Регистрация"
                    onFocusCapture={recordFirstInteraction}
                    onInputCapture={recordFirstInteraction}
                    onSubmit={handleSubmit(onSubmit)}
                >
                    {(publicLaunchIntent || readIntentHandoff('draft')) && (
                        <Box
                            mb={2}
                            px={1.5}
                            py={1.25}
                            borderRadius={1.5}
                            bgcolor="rgba(0, 68, 255, 0.06)"
                        >
                            <Typography
                                variant="body2"
                                color="text.primary"
                            >
                                {readIntentHandoff('draft')
                                    ? 'Описание сохранено. После входа продолжите настройку потока.'
                                    : `Выбрано: ${publicLaunchIntent?.packageSize} лидов. После входа продолжите настройку потока.`}
                            </Typography>
                        </Box>
                    )}
                    <Box mb={2}>
                        <Controller
                            name="email"
                            control={control}
                            disabled={isLoading}
                            render={({ field }) => (
                                <LoginField
                                    {...field}
                                    label="Почта"
                                    placeholder="Введите email"
                                    error={errors?.email?.message}
                                />
                            )}
                        />
                    </Box>
                    <Box mb={2}>
                        <Controller
                            name="password"
                            control={control}
                            render={({ field }) => (
                                <PasswordField
                                    {...field}
                                    label="Пароль"
                                    placeholder="Введите пароль"
                                    error={errors?.password?.message}
                                />
                            )}
                            disabled={isLoading}
                        />
                    </Box>
                    <Box mb={2}>
                        <Controller
                            name="password2"
                            control={control}
                            disabled={isLoading}
                            render={({ field }) => (
                                <PasswordField
                                    {...field}
                                    label="Подтверждение"
                                    placeholder="Повторите пароль"
                                    error={errors?.password2?.message}
                                />
                            )}
                        />
                    </Box>
                    <Box mb={2}>
                        <CustomAccordion title="Как создать пароль">
                            <Box>
                                <Typography
                                    variant="body2"
                                    color="text.primary"
                                >
                                    Для создания пароля разрешается применять следующие символы:
                                </Typography>
                                <ul style={{ listStyle: 'outside' }}>
                                    <li>
                                        <Typography
                                            variant="body2"
                                            color="text.primary"
                                        >
                                            Заглавные латинские буквы: от A до Z (26 символов)
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography
                                            variant="body2"
                                            color="text.primary"
                                        >
                                            Строчные латинские буквы: от a до z (26 символов)
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography
                                            variant="body2"
                                            color="text.primary"
                                        >
                                            Цифры от 0 до 9 (10 символов)
                                        </Typography>
                                    </li>
                                    <li>
                                        <Typography
                                            variant="body2"
                                            color="text.primary"
                                        >
                                            {
                                                'Символы: (пробел) ! " # $ % & \' ( ) * + , - . / : ; < = > ? @ [  ] ^ _` { | } ~ (33 символа)'
                                            }
                                        </Typography>
                                    </li>
                                </ul>
                            </Box>
                        </CustomAccordion>
                    </Box>
                    {turnstileSiteKey && (
                        <Box
                            mb={2}
                            display="flex"
                            justifyContent="center"
                        >
                            <TurnstileWidget
                                key={turnstileResetKey}
                                siteKey={turnstileSiteKey}
                                onTokenChange={setCaptchaToken}
                            />
                        </Box>
                    )}
                    <Box mb={1.5} display="grid" gridTemplateColumns="20px minmax(0, 1fr)" columnGap={1.5} alignItems="start">
                        <Checkbox
                            id="register-analytics-consent"
                            size="small"
                            sx={{ p: 0, mt: '2px' }}
                            checked={analyticsConsent}
                            onChange={(event) => changeAnalyticsConsent(event.target.checked)}
                            disabled={isLoading}
                        />
                        <Typography component="label" htmlFor="register-analytics-consent" variant="body2">
                            Разрешить аналитику, чтобы улучшать сайт
                        </Typography>
                    </Box>
                    <Box mb={2} display="grid" gridTemplateColumns="20px minmax(0, 1fr)" columnGap={1.5} alignItems="start">
                        <Checkbox
                            id="register-marketing-consent"
                            size="small"
                            sx={{ p: 0, mt: '2px' }}
                            checked={marketingConsent}
                            onChange={(event) => setMarketingConsent(event.target.checked)}
                            disabled={isLoading}
                            inputProps={{ 'aria-label': 'Хочу получать предложения Лидсмании по email' }}
                        />
                        <Typography component="label" htmlFor="register-marketing-consent" variant="body2">
                            Хочу получать предложения Лидсмании по email.{' '}
                            <Link href="https://leadsmania.ru/marketing-consent/" target="_blank" rel="noopener noreferrer">
                                Согласие на рассылку
                            </Link>
                        </Typography>
                    </Box>
                    <Box mb={2}>
                        <Button
                            disabled={isLoading || !isValid || Boolean(turnstileSiteKey && !captchaToken)}
                            variant="contained"
                            size="large"
                            type="submit"
                            fullWidth
                        >
                            Создать аккаунт
                        </Button>
                    </Box>
                    <Box>
                        <Typography
                            variant="body2"
                            color="textDisabled"
                            textAlign="center"
                            fontWeight={400}
                        >
                            Нажимая «Создать аккаунт», вы принимаете{' '}
                            <Link
                                textAlign="center"
                                color="inherit"
                                component={'a'}
                                href={'https://leadsmania.ru/terms/'}
                                target="_blank"
                                rel="noopener noreferrer"
                                fontWeight={400}
                            >
                                пользовательское соглашение
                            </Link>
                            {' '}и подтверждаете, что ознакомились с{' '}
                            <Link color="inherit" href="https://leadsmania.ru/privacy/" target="_blank" rel="noopener noreferrer">
                                политикой обработки персональных данных
                            </Link>
                            .
                        </Typography>
                    </Box>
                </form>
            </WidgetWrapper>
            <Box
                mt={0.5}
                display="flex"
                justifyContent="center"
            >
                <Link
                    textAlign="center"
                    variant="caption"
                    color="primary"
                    component={RouterLink}
                    to={ROUTES.LOGIN.path}
                >
                    Есть аккаунт? Войти
                </Link>
            </Box>
        </LoadingWrapper>
    );
}
